/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Track Web Audio DAW & Timeline Mixer (Task 10.5.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useRef, useEffect } from 'react';
import {
  Mic,
  Music,
  Volume2,
  VolumeX,
  Zap,
  TrendingDown,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useAudioMixerStore } from '../../stores/useAudioMixerStore';

interface WebAudioTimelineMixerProps {
  onOpenExport?: () => void;
}

export const WebAudioTimelineMixer: React.FC<WebAudioTimelineMixerProps> = ({
  onOpenExport,
}) => {
  const { t } = useTranslation();
  const {
    tracks,
    totalDuration,
    currentTime,
    isPlaying,
    zoomLevel,
    duckingSettings,
    duckingRegions,
    setTrackVolume,
    setTrackPan,
    toggleTrackMute,
    toggleTrackSolo,
    seekTimeline,
    pauseTimeline,
  } = useAudioMixerStore();

  const timelineContainerRef = useRef<HTMLDivElement>(null);

  // Time-to-pixel ratio
  const basePixelsPerSec = 28 * zoomLevel;
  const timelinePixelWidth = Math.max(800, totalDuration * basePixelsPerSec);

  // Playhead auto-advancement simulation
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    if (isPlaying) {
      interval = setInterval(() => {
        const nextTime = currentTime + 0.1;
        if (nextTime >= totalDuration) {
          seekTimeline(0);
          pauseTimeline();
        } else {
          seekTimeline(+nextTime.toFixed(2));
        }
      }, 100);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, currentTime, totalDuration, seekTimeline, pauseTimeline]);

  // Handle click on timeline to seek
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const targetTime = clickX / basePixelsPerSec;
    seekTimeline(Math.max(0, Math.min(totalDuration, +targetTime.toFixed(2))));
  };

  // Generate ruler markers every 5 seconds
  const markerStep = zoomLevel >= 2 ? 2 : 5;
  const rulerMarkers: number[] = [];
  for (let s = 0; s <= totalDuration + 5; s += markerStep) {
    rulerMarkers.push(s);
  }

  const formatRulerTime = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const remainder = Math.floor(sec % 60);
    return `${mins.toString().padStart(2, '0')}:${remainder.toString().padStart(2, '0')}`;
  };

  // Check if BGM is currently ducked at currentTime
  const isCurrentlyDucked =
    duckingSettings.enabled &&
    duckingRegions.some(
      (r) => currentTime >= r.startSec && currentTime <= r.endSec
    );

  return (
    <div className="space-y-4">
      {/* Smart Ducking Real-time Status Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-2xl bg-surface border border-surface-border text-xs">
        <div className="flex items-center gap-2.5">
          <div
            className={`p-2 rounded-xl flex items-center justify-center transition-colors ${
              isCurrentlyDucked
                ? 'bg-amber-500/10 text-amber-500 border border-amber-500/30 animate-pulse'
                : 'bg-primary/10 text-primary border border-primary/20'
            }`}
          >
            <TrendingDown className="size-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-foreground">
                {t('mixer.smartDuckingStatus')}:
              </span>
              <span
                className={`font-semibold px-2 py-0.5 rounded-full text-[10px] ${
                  isCurrentlyDucked
                    ? 'bg-amber-500/15 text-amber-500 border border-amber-500/30'
                    : 'bg-surface-elevated text-foreground-muted'
                }`}
              >
                {isCurrentlyDucked
                  ? t('mixer.duckingActiveIndicator', { percent: duckingSettings.duckingPercent })
                  : t('mixer.duckingIdleIndicator')}
              </span>
            </div>
            <p className="text-[11px] text-foreground-muted mt-0.5">
              {t('mixer.duckingExplanation', {
                percent: duckingSettings.duckingPercent,
                fade: duckingSettings.fadeDurationMs,
                recovery: duckingSettings.recoveryDurationMs,
              })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
          <span className="text-[11px] text-foreground-muted font-mono font-medium">
            {duckingRegions.length} {t('mixer.duckingZonesCount')}
          </span>
          {onOpenExport && (
            <button
              type="button"
              onClick={onOpenExport}
              className="px-3 py-1.5 rounded-lg bg-primary text-primary-fg text-xs font-semibold hover:bg-primary/90 shadow-xs transition-colors"
            >
              {t('mixer.exportMixBtn')}
            </button>
          )}
        </div>
      </div>

      {/* Main Multi-Track DAW Canvas Container */}
      <div className="rounded-2xl bg-surface border border-surface-border shadow-xs overflow-hidden flex flex-col">
        {/* Top Split: Track Controls Header (Left 220px) + Timeline Ruler (Right) */}
        <div className="flex border-b border-surface-border bg-surface-elevated/70 select-none">
          {/* Left Track Control Header */}
          <div className="w-56 sm:w-64 p-3 border-r border-surface-border flex items-center justify-between shrink-0">
            <span className="text-[11px] font-bold uppercase tracking-wider text-foreground-muted">
              {t('mixer.trackControlsLabel')}
            </span>
            <span className="text-[10px] font-mono text-foreground-muted">
              {tracks.length} {t('mixer.tracksCountLabel')}
            </span>
          </div>

          {/* Right Ruler Area */}
          <div className="flex-1 overflow-x-auto relative no-scrollbar" ref={timelineContainerRef}>
            <div
              onClick={handleTimelineClick}
              className="h-9 relative cursor-pointer"
              style={{ width: `${timelinePixelWidth}px` }}
            >
              {rulerMarkers.map((sec) => (
                <div
                  key={sec}
                  className="absolute top-0 bottom-0 border-l border-surface-border/60 flex flex-col justify-between pl-1"
                  style={{ left: `${sec * basePixelsPerSec}px` }}
                >
                  <span className="text-[10px] font-mono text-foreground-muted font-medium">
                    {formatRulerTime(sec)}
                  </span>
                  <div className="h-1.5 w-px bg-surface-border" />
                </div>
              ))}

              {/* Playhead Marker on Ruler */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-primary z-20 pointer-events-none transition-all duration-75"
                style={{ left: `${currentTime * basePixelsPerSec}px` }}
              >
                <div className="size-2 -ml-[3px] bg-primary rotate-45" />
              </div>
            </div>
          </div>
        </div>

        {/* Tracks List */}
        <div className="divide-y divide-surface-border flex flex-col">
          {tracks.map((track) => {
            const isTrackBgm = track.type === 'bgm';

            return (
              <div key={track.id} className="flex min-h-[96px] group/track relative">
                {/* Track Controls Strip (Left 220px) */}
                <div className="w-56 sm:w-64 p-3 border-r border-surface-border bg-surface/80 flex flex-col justify-between shrink-0 space-y-2">
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="p-1 rounded-md bg-surface-elevated text-foreground-muted">
                        {track.type === 'dialogue' ? (
                          <Mic className="size-3.5 text-primary" />
                        ) : track.type === 'bgm' ? (
                          <Music className="size-3.5 text-amber-500" />
                        ) : (
                          <Zap className="size-3.5 text-emerald-500" />
                        )}
                      </div>
                      <span className="text-xs font-bold text-foreground truncate">
                        {track.name}
                      </span>
                    </div>

                    {/* Mute & Solo Buttons */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => toggleTrackMute(track.id)}
                        className={`size-6 rounded text-[11px] font-bold font-mono transition-colors ${
                          track.muted
                            ? 'bg-red-500 text-white'
                            : 'bg-surface-elevated text-foreground-muted hover:text-foreground'
                        }`}
                        title={t('mixer.muteTrack')}
                      >
                        M
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleTrackSolo(track.id)}
                        className={`size-6 rounded text-[11px] font-bold font-mono transition-colors ${
                          track.solo
                            ? 'bg-amber-500 text-black'
                            : 'bg-surface-elevated text-foreground-muted hover:text-foreground'
                        }`}
                        title={t('mixer.soloTrack')}
                      >
                        S
                      </button>
                    </div>
                  </div>

                  {/* Volume Fader & Pan Slider */}
                  <div className="space-y-1.5">
                    {/* Volume Slider */}
                    <div className="flex items-center gap-1.5 text-[10px] text-foreground-muted">
                      {track.volume === 0 || track.muted ? (
                        <VolumeX className="size-3 shrink-0" />
                      ) : (
                        <Volume2 className="size-3 shrink-0" />
                      )}
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={track.volume}
                        disabled={track.muted}
                        onChange={(e) =>
                          setTrackVolume(track.id, parseFloat(e.target.value))
                        }
                        className="w-full h-1 accent-primary cursor-pointer disabled:opacity-40"
                      />
                      <span className="font-mono w-7 text-right">
                        {Math.round(track.volume * 100)}%
                      </span>
                    </div>

                    {/* Pan Slider */}
                    <div className="flex items-center justify-between text-[10px] text-foreground-muted font-mono">
                      <span>L</span>
                      <input
                        type="range"
                        min="-1"
                        max="1"
                        step="0.1"
                        value={track.pan}
                        onChange={(e) =>
                          setTrackPan(track.id, parseFloat(e.target.value))
                        }
                        className="w-24 h-1 accent-primary cursor-pointer"
                      />
                      <span>R</span>
                    </div>
                  </div>
                </div>

                {/* Track Clips Lane (Right) */}
                <div
                  onClick={handleTimelineClick}
                  className="flex-1 overflow-x-auto relative cursor-pointer bg-surface/30 hover:bg-surface/50 transition-colors py-2"
                >
                  <div
                    className="h-full relative min-h-[80px]"
                    style={{ width: `${timelinePixelWidth}px` }}
                  >
                    {/* Background grid vertical lines */}
                    {rulerMarkers.map((sec) => (
                      <div
                        key={sec}
                        className="absolute top-0 bottom-0 border-l border-surface-border/20 pointer-events-none"
                        style={{ left: `${sec * basePixelsPerSec}px` }}
                      />
                    ))}

                    {/* Smart Ducking Shaded Regions on BGM track */}
                    {isTrackBgm &&
                      duckingSettings.enabled &&
                      duckingRegions.map((region, idx) => {
                        const regionLeft = region.startSec * basePixelsPerSec;
                        const regionWidth = (region.endSec - region.startSec) * basePixelsPerSec;
                        return (
                          <div
                            key={idx}
                            title={`${t('mixer.duckedTo')} ${Math.round(region.duckingRatio * 100)}% (${region.speechTrackName})`}
                            className="absolute top-0 bottom-0 bg-amber-500/10 border-x border-dashed border-amber-500/30 rounded flex items-center justify-center pointer-events-none z-10"
                            style={{
                              left: `${regionLeft}px`,
                              width: `${regionWidth}px`,
                            }}
                          >
                            <span className="text-[10px] font-bold text-amber-500/80 uppercase tracking-wider px-1 bg-surface/80 rounded">
                              Ducked {Math.round(region.duckingRatio * 100)}%
                            </span>
                          </div>
                        );
                      })}

                    {/* Audio Clips */}
                    {track.clips.map((clip) => {
                      const clipLeft = clip.startOffsetSec * basePixelsPerSec;
                      const clipWidth = Math.max(30, clip.durationSec * basePixelsPerSec);

                      return (
                        <div
                          key={clip.id}
                          className={`absolute top-1 bottom-1 rounded-xl p-2 border flex flex-col justify-between shadow-xs transition-transform hover:scale-[1.01] ${
                            track.type === 'dialogue'
                              ? 'bg-primary/10 border-primary/30 text-foreground'
                              : track.type === 'bgm'
                              ? 'bg-amber-500/10 border-amber-500/30 text-foreground'
                              : 'bg-emerald-500/10 border-emerald-500/30 text-foreground'
                          }`}
                          style={{
                            left: `${clipLeft}px`,
                            width: `${clipWidth}px`,
                          }}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-[11px] font-bold truncate">
                              {clip.name}
                            </span>
                            <span className="text-[9px] font-mono font-medium text-foreground-muted px-1.5 py-0.2 rounded bg-surface/60 shrink-0">
                              {clip.durationSec.toFixed(1)}s
                            </span>
                          </div>

                          {/* Mini Waveform Bars Simulation inside Clip */}
                          <div className="h-6 flex items-end gap-0.5 overflow-hidden opacity-60">
                            {Array.from({ length: Math.min(36, Math.floor(clipWidth / 5)) }).map(
                              (_, barIdx) => {
                                const heightPercent = 25 + ((barIdx * 19) % 70);
                                return (
                                  <div
                                    key={barIdx}
                                    className={`w-1 rounded-full ${
                                      track.type === 'dialogue'
                                        ? 'bg-primary'
                                        : track.type === 'bgm'
                                        ? 'bg-amber-500'
                                        : 'bg-emerald-500'
                                    }`}
                                    style={{ height: `${heightPercent}%` }}
                                  />
                                );
                              }
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Hairline Playhead Scrubber */}
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-primary z-30 pointer-events-none transition-all duration-75 shadow-xs"
                      style={{ left: `${currentTime * basePixelsPerSec}px` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
